import jsPDF from 'jspdf';
import { Audit, AuditFinding, AuditEvidence } from '@/firebase/auditService';

interface AuditPdfPayload {
  audit: Audit;
  organizationName: string;
  complianceTemplates: Array<{ id: string; name: string }>;
}

const PAGE_TOP = 20;
const LEFT_MARGIN = 20;
const PAGE_BOTTOM = 280;
const LINE_HEIGHT = 7;

function addLine(doc: jsPDF, text: string | string[], yRef: { value: number }, options?: { bold?: boolean; fontSize?: number }) {
  if (yRef.value > PAGE_BOTTOM) {
    doc.addPage();
    yRef.value = PAGE_TOP;
  }

  if (options?.fontSize) {
    doc.setFontSize(options.fontSize);
  } else {
    doc.setFontSize(10);
  }

  if (options?.bold) {
    doc.setFont('helvetica', 'bold');
  } else {
    doc.setFont('helvetica', 'normal');
  }

  const lines = Array.isArray(text) ? text : [text];
  lines.forEach((line) => {
    doc.text(line, LEFT_MARGIN, yRef.value);
    yRef.value += LINE_HEIGHT;
  });
}

function formatDate(timestamp: any): string {
  if (!timestamp) return 'N/A';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    compliant: 'Compliant',
    'non-compliant': 'Non-Compliant',
    partial: 'Partial',
    not_applicable: 'Not Applicable',
    pending: 'Pending',
    planned: 'Planned',
    in_progress: 'In Progress',
    completed: 'Completed',
    overdue: 'Overdue',
    cancelled: 'Cancelled'
  };
  return labels[status] || status;
}

export function generateAuditPdf(payload: AuditPdfPayload): Blob {
  const { audit, organizationName, complianceTemplates } = payload;
  const doc = new jsPDF();
  const yRef = { value: PAGE_TOP };

  // Title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Audit Report', LEFT_MARGIN, yRef.value);
  yRef.value += 10;

  // Organization
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(`Organization: ${organizationName}`, LEFT_MARGIN, yRef.value);
  yRef.value += LINE_HEIGHT;

  // Audit Information
  yRef.value += 5;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Audit Information', LEFT_MARGIN, yRef.value);
  yRef.value += LINE_HEIGHT + 2;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  addLine(doc, `Audit Template: ${audit.auditTemplateName}`, yRef);
  addLine(doc, `Location: ${audit.locationName}`, yRef);
  addLine(doc, `Status: ${getStatusLabel(audit.status)}`, yRef);
  addLine(doc, `Frequency: ${audit.frequency}`, yRef);
  addLine(doc, `Scheduled Date: ${formatDate(audit.scheduledDate)}`, yRef);
  addLine(doc, `Due Date: ${formatDate(audit.dueDate)}`, yRef);
  
  if (audit.completedAt) {
    addLine(doc, `Completed Date: ${formatDate(audit.completedAt)}`, yRef);
  }
  
  addLine(doc, `Completion Percentage: ${audit.completionPercentage || 0}%`, yRef);
  yRef.value += 5;

  // Summary Section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  addLine(doc, 'Summary', yRef);
  yRef.value += 2;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  addLine(doc, `Total Compliance Templates: ${audit.complianceTemplateIds?.length || 0}`, yRef);
  addLine(doc, `Total Tasks: ${audit.associatedTaskIds?.length || 0}`, yRef);
  addLine(doc, `Total Findings: ${audit.findings?.length || 0}`, yRef);
  addLine(doc, `Total Evidence: ${audit.evidence?.length || 0}`, yRef);
  yRef.value += 5;

  // Findings Section
  if (audit.findings && audit.findings.length > 0) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    addLine(doc, 'Findings', yRef);
    yRef.value += 2;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    audit.findings.forEach((finding, index) => {
      const templateName = complianceTemplates.find(t => t.id === finding.complianceTemplateId)?.name || finding.complianceTemplateName;
      
      addLine(doc, `${index + 1}. ${templateName}`, yRef, { bold: true });
      addLine(doc, `   Status: ${getStatusLabel(finding.status)}`, yRef);
      
      if (finding.notes) {
        const wrappedNotes = doc.splitTextToSize(`   Notes: ${finding.notes}`, 170);
        addLine(doc, wrappedNotes, yRef);
      }
      
      if (finding.reviewedBy) {
        addLine(doc, `   Reviewed By: ${finding.reviewedBy}`, yRef);
      }
      
      if (finding.reviewedAt) {
        addLine(doc, `   Reviewed At: ${formatDate(finding.reviewedAt)}`, yRef);
      }
      
      yRef.value += 3;
    });
  }

  // Evidence Section
  if (audit.evidence && audit.evidence.length > 0) {
    yRef.value += 5;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    addLine(doc, 'Evidence', yRef);
    yRef.value += 2;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    // Group evidence by type
    const evidenceByType = audit.evidence.reduce((acc, evidence) => {
      const type = evidence.evidenceType || 'unknown';
      if (!acc[type]) acc[type] = [];
      acc[type].push(evidence);
      return acc;
    }, {} as Record<string, AuditEvidence[]>);

    Object.entries(evidenceByType).forEach(([type, evidences]) => {
      const typeLabel = type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
      addLine(doc, `${typeLabel} (${evidences.length})`, yRef, { bold: true });
      
      evidences.forEach((evidence) => {
        const name = evidence.sourceName || evidence.fileName || 'Evidence';
        addLine(doc, `  • ${name}`, yRef);
        if (evidence.uploadedAt) {
          addLine(doc, `    Uploaded: ${formatDate(evidence.uploadedAt)}`, yRef);
        }
      });
      
      yRef.value += 2;
    });
  }

  // Notes Section
  if (audit.notes) {
    yRef.value += 5;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    addLine(doc, 'Notes', yRef);
    yRef.value += 2;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const wrappedNotes = doc.splitTextToSize(audit.notes, 170);
    addLine(doc, wrappedNotes, yRef);
  }

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(128, 128, 128);
    doc.text(
      `Page ${i} of ${pageCount} | Generated: ${new Date().toLocaleString()}`,
      LEFT_MARGIN,
      doc.internal.pageSize.getHeight() - 10
    );
    doc.setTextColor(0, 0, 0);
  }

  return doc.output('blob');
}

